// src/components/common/BreakdownDisplay.js
import React from 'react';
import ImpositionVisualizer from '../../components/small-format/ImpositionVisualizer'; // Import the reusable visualizer
import { getLargeFormatLaminateLabel, hasMaterialIncludedLaminate } from '../../utils/largeFormatFinishing';

// Helper to format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }
  return `£${value.toFixed(2)}`;
};

const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }
  return value.toFixed(decimals);
};

const renderVolumeDiscountEntry = (entry, idx) => {
  if (entry?.type === 'area_curve') {
    return (
      <React.Fragment key={`vol-disc-${idx}`}>
        <dt className="text-gray-600 pl-2 text-[10px]">
          └ {entry.job_type} / {entry.materials}: {formatNumber(entry.group_area_sqm, 2)} sqm @ {formatNumber(entry.discount_percent, 2)}% off
        </dt>
        <dd className="text-right text-gray-600 text-[10px]">
          -£{formatNumber(entry.discount_amount, 2)}
        </dd>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment key={`vol-disc-${idx}`}>
      <dt className="text-gray-600 pl-2 text-[10px]">
        └ {entry.tier_range} items ({entry.items_in_tier} @ {entry.discount_percent}% off):
      </dt>
      <dd className="text-right text-gray-600 text-[10px]">
        £{formatNumber(entry.tier_cost, 2)}
      </dd>
    </React.Fragment>
  );
};

const BreakdownDisplay = ({ breakdown, itemType, pricingTier = 'retail', item = {} }) => { // Added pricingTier parameter
  if (!breakdown || Object.keys(breakdown).length === 0) {
    console.log("BreakdownDisplay received:", JSON.stringify(breakdown, null, 2));
    return <p className="text-gray-500">No breakdown data available.</p>;
  }

  const hasPieces = Array.isArray(breakdown.pieces) && breakdown.pieces.length > 0;
  const isCutVinylBreakdown = hasPieces && itemType === 'cut-vinyl';
  const materialIncludedLaminate = hasMaterialIncludedLaminate(breakdown);
  const largeFormatFinishes = (breakdown.finishing_processes || []).filter(process =>
    !materialIncludedLaminate || !(process.id === 'laminate' || /laminat/i.test(process.name)),
  );

  // Determine if this is a retail/partner tier (which uses curve margin) vs trade tier (which uses volume discounts)
  const isRetailOrPartner = pricingTier === 'retail' || pricingTier === 'partner';

  // Destructure core details - ensure these keys match your backend output
  const {
    material_cost, // Base cost for inner pages
    marked_up_material_cost, // Marked up cost for inner pages
    click_cost,
    print_cost,
    total_sides_for_click, // For NCR
    total_sides,
    finishing,
    misc_costs,
    setup_cost,
    // Use the potentially renamed key, fallback to old name
    inner_page_imposition_details, // Preferred key from backend update
    imposition, // Fallback key
    total_inner_sheets,
    min_charge_applied, // Likely for Small Format (boolean)
    // Large Format specific fields from price_calculator.py
    job_setup_charge,
    sub_total_before_min_charge,
    minimum_charge_threshold,
    minimum_charge_applied_adjustment,
    // Destructure cover details if they exist
    cover_stock_details, // Object containing cover stock info { name, sheets_needed, base_cost, total_cost }
    cover_imposition, // Imposition details specifically for the cover
    total_cover_sheets, // Explicit count of cover sheets
  // Cut Vinyl specific fields
  total_pieces,
  total_items,
  pricing_tier,
  partner_tier_details,
    // Curve margin fields for retail/partner
    curve_margin_applied,
    curve_margin_percent,
    base_cost_for_curve_margin,
    marked_up_cost_with_curve,
    // Base click cost for retail/partner
    base_total_click_cost
  } = breakdown;

  const finishingDetails = breakdown.finishing_details || {};
  const innerFinishesDetailed = Array.isArray(finishingDetails.inner_finishes) ? finishingDetails.inner_finishes : [];
  const coverFinishesDetailed = Array.isArray(finishingDetails.cover_finishes) ? finishingDetails.cover_finishes : [];
  const hasInnerFinishesDetailed = innerFinishesDetailed.length > 0;
  const hasCoverFinishesDetailed = coverFinishesDetailed.length > 0;
  const innerFinishingOptions = finishingDetails.inner_finishing_options || {};
  const coverIsDoubleSidedDetail =
    breakdown.cover_is_double_sided ?? breakdown.coverIsDoubleSided ?? null;
  const breakdownColours = breakdown.colours || breakdown.color;
  const coverColoursDetail =
    breakdown.cover_colours || breakdown.coverColours || null;
  const innerDoubleSided =
    breakdown.is_double_sided ?? breakdown.isDoubleSided ?? null;
  const linearMinimums = breakdown.linear_minimums_applied || [];

  const formatOptionKey = (key) => {
    if (!key || typeof key !== 'string') return key;
    return key
      .split('_')
      .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : ''))
      .join(' ')
      .trim();
  };

  const formatFinishOptions = (finish) => {
    if (!finish || typeof finish !== 'object') {
      return '';
    }

    const displayOptions = finish.selected_options_display && typeof finish.selected_options_display === 'object'
      ? finish.selected_options_display
      : {};
    const rawOptions = finish.selected_options && typeof finish.selected_options === 'object'
      ? finish.selected_options
      : {};

    const optionKeys = new Set([
      ...Object.keys(displayOptions),
      ...Object.keys(rawOptions)
    ]);

    const entries = [];
    const skipKeys = new Set(['cover_type']);

    optionKeys.forEach((key) => {
      if (!key || skipKeys.has(key)) return;
      const displayValue = displayOptions[key];
      const rawValue = rawOptions[key];
      const valueToUse = displayValue !== undefined && displayValue !== null && displayValue !== ''
        ? displayValue
        : rawValue;
      if (valueToUse === null || valueToUse === '' || valueToUse === false) return;
      entries.push(`${formatOptionKey(key)}: ${valueToUse}`);
    });

    return entries.join(', ');
  };

  const formatColourDescriptor = (value) => {
    if (!value) return null;
    const normalized = value.toLowerCase();
    if (normalized === 'full_colour' || normalized === 'full_color') return 'Full Colour';
    if (normalized === 'black_white' || normalized === 'mono') return 'Black & White';
    return value;
  };

  const printingNotes = [];
  if (innerDoubleSided === true && coverIsDoubleSidedDetail === false) {
    printingNotes.push('Inners print double-sided; covers print single-sided.');
  } else if (innerDoubleSided === false && coverIsDoubleSidedDetail === true) {
    printingNotes.push('Inners print single-sided; covers print double-sided.');
  }

  const innerColourText = formatColourDescriptor(breakdownColours);
  const coverColourText = formatColourDescriptor(coverColoursDetail);
  if (
    innerColourText &&
    coverColourText &&
    innerColourText !== coverColourText
  ) {
    printingNotes.push(`Inner pages: ${innerColourText}; covers: ${coverColourText}.`);
  }

  // Determine which imposition data to use for inner pages
  const innerImpositionData = inner_page_imposition_details || imposition;

  // Calculate inner page markup
  const materialMarkup = (marked_up_material_cost ?? 0) - (material_cost ?? 0);

  // --- Cut Vinyl Specific Breakdown ---
  if (isCutVinylBreakdown) {
    return (
      <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs space-y-3">
        {/* Cut Vinyl Summary */}
        <div className="pb-2 mb-2 border-b border-gray-200">
          <h5 className="font-medium text-gray-600 mb-1">Cut Vinyl Summary:</h5>
          <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
            <dt className="text-gray-600">Total Pieces:</dt>
            <dd className="text-right">{total_pieces || breakdown.pieces.length}</dd>
            <dt className="text-gray-600">Total Items:</dt>
            <dd className="text-right">{total_items || breakdown.pieces.reduce((sum, p) => sum + p.quantity, 0)}</dd>
            {pricing_tier && (
              <>
                <dt className="text-gray-600">Pricing Tier:</dt>
                <dd className="text-right capitalize">{pricing_tier}</dd>
              </>
            )}
            {linearMinimums.length > 0 && (
              <>
                <dt className="text-gray-600">Linear Minimum Uplift:</dt>
                <dd className="text-right">
                  {linearMinimums.map((entry, idx) => (
                    <div key={idx}>
                      {entry.name || entry.material_id}: +{formatNumber(entry.delta_length_m, 1)}m (£
                      {formatCurrency(entry.delta_requested).replace('£', '')})
                    </div>
                  ))}
                </dd>
              </>
            )}
          </dl>
        </div>

        {/* Per-Piece Breakdown */}
        <div>
          <h5 className="font-medium text-gray-600 mb-1">Per-Piece Breakdown:</h5>
          <div className="space-y-2">
            {breakdown.pieces.map((piece, index) => (
              <div key={index} className="bg-white p-2 rounded border border-gray-200">
                <div className="flex justify-between items-start mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Piece {piece.piece_number || index + 1}</span>
                    {piece.material?.color_hex && (
                      <div 
                        className="w-4 h-4 rounded border border-gray-300"
                        style={{ backgroundColor: piece.material.color_hex }}
                        title={piece.material.name}
                      />
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-medium">
                      {formatCurrency(piece.total_cost ?? piece.piece_cost ?? piece.sub_total_before_min_charge)}
                    </div>
                    {piece.quantity !== undefined && (
                      <div className="text-gray-500 text-[10px]">
                        {piece.quantity} × {formatCurrency(piece.piece_cost ?? piece.sub_total_before_min_charge)}
                      </div>
                    )}
                  </div>
                </div>

                <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2 text-[10px]">
                  <dt className="text-gray-600">Material:</dt>
                  <dd className="text-right">
                    {piece.material?.name ||
                      (piece.materials?.map((m) => m.name).join(', ')) ||
                      'Unknown'}
                  </dd>
                  {Array.isArray(piece.materials) &&
                    piece.materials.some((m) => m.pricing_method === 'linear_m') && (
                      <>
                        <dt className="text-gray-600">Linear Length:</dt>
                        <dd className="text-right">
                          {piece.materials
                            .filter((m) => m.pricing_method === 'linear_m')
                            .map((m, idx) => (
                              <div key={idx}>
                                {formatNumber(m.length_m_used ?? m.length_m, 3)}m
                                {m.planned_length_m !== undefined && (
                                  <> (planned {formatNumber(m.planned_length_m, 3)}m)</>
                                )}
                              </div>
                            ))}
                        </dd>
                      </>
                    )}
                  <dt className="text-gray-600">Dimensions:</dt>
                  <dd className="text-right">
                    {piece.dimensions ||
                      (piece.width_mm && piece.height_mm
                        ? `${piece.width_mm} × ${piece.height_mm}mm`
                        : '—')}
                  </dd>
                  <dt className="text-gray-600">Area:</dt>
                  <dd className="text-right">
                    {piece.area_sqm ? `${piece.area_sqm} m²` : '—'}
                  </dd>
                  <dt className="text-gray-600">Material Cost:</dt>
                  <dd className="text-right">
                    {piece.material_cost !== undefined
                      ? formatCurrency(piece.material_cost)
                      : formatCurrency(piece.materials_cost)}
                  </dd>
                </dl>

                {/* Finishing Breakdown for this piece */}
                {piece.finishing_breakdown && piece.finishing_breakdown.length > 0 && (
                  <div className="mt-1 pt-1 border-t border-gray-100">
                    <h6 className="font-medium text-gray-600 mb-0.5">Finishing:</h6>
                    <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2 text-[10px]">
                      {piece.finishing_breakdown.map((finish, finishIndex) => (
                        <React.Fragment key={finishIndex}>
                          <dt className="text-gray-600">
                            {finish.name}
                            {finish.weeding_complexity_label && (
                              <span className="text-gray-500 font-normal text-xs ml-1">
                                (Complexity: {finish.weeding_complexity_label})
                              </span>
                            )}
                            :
                          </dt>
                          <dd className="text-right">{formatCurrency(finish.total_cost)}</dd>
                          {finish.setup_cost > 0 && (
                            <>
                              <dt className="text-gray-500 pl-2">└ Setup:</dt>
                              <dd className="text-right">{formatCurrency(finish.setup_cost)}</dd>
                            </>
                          )}
                          {finish.run_cost > 0 && (
                            <>
                              <dt className="text-gray-500 pl-2">└ Run:</dt>
                              <dd className="text-right">{formatCurrency(finish.run_cost)}</dd>
                            </>
                          )}
                        </React.Fragment>
                      ))}
                    </dl>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

                 {/* Partner Tier Details */}
         {partner_tier_details && (
           <div className="pt-2 mt-2 border-t border-gray-200">
             <h5 className="font-medium text-gray-600 mb-1">Partner Discount:</h5>
             <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
               <dt className="text-gray-600">Original Retail Price:</dt>
               <dd className="text-right">{formatCurrency(partner_tier_details.original_retail_price)}</dd>
               <dt className="text-green-600">Partner Discount ({partner_tier_details.discount_percent || ((1 - partner_tier_details.discount_factor) * 100).toFixed(1)}%):</dt>
               <dd className="text-right text-green-600">-{formatCurrency(partner_tier_details.original_retail_price - partner_tier_details.price_after_discount_before_min_charge)}</dd>
             </dl>
           </div>
         )}

        {/* Minimum Charge */}
        {breakdown.min_charge_applied && (
          <div className="pt-2 mt-2 border-t border-gray-200">
            <p className="text-orange-600 font-medium text-center">
              Minimum charge applied.
            </p>
          </div>
        )}

        {/* Total Summary */}
        <div className="pt-2 mt-2 border-t border-gray-200">
          <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5">
            <dt className="text-gray-600 font-medium">Subtotal:</dt>
            <dd className="text-right font-medium">{formatCurrency(breakdown.total_cost)}</dd>
            <dt className="text-gray-800 font-semibold">Final Price:</dt>
            <dd className="text-right font-semibold">{formatCurrency(breakdown.final_cost ?? breakdown.total_cost)}</dd>
          </dl>
        </div>
      </div>
    );
  }

  // --- Multi-piece large format breakdown ---
  if (hasPieces) {
    return (
      <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs space-y-3">
        <div className="pb-2 mb-2 border-b border-gray-200">
          <h5 className="font-medium text-gray-600 mb-1">Multi-Piece Summary:</h5>
          <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
            <dt className="text-gray-600">Total Pieces:</dt>
            <dd className="text-right">{breakdown.total_pieces}</dd>
            <dt className="text-gray-600">Total Items:</dt>
            <dd className="text-right">{breakdown.total_items}</dd>
            {breakdown.pricing_tier && (
              <>
                <dt className="text-gray-600">Pricing Tier:</dt>
                <dd className="text-right capitalize">{breakdown.pricing_tier}</dd>
              </>
            )}
          </dl>
        </div>

        <div>
          <h5 className="font-medium text-gray-600 mb-1">Per-Piece Breakdown:</h5>
          <div className="space-y-2">
            {breakdown.pieces.map((piece, index) => (
              <div key={index} className="bg-white p-2 rounded border border-gray-200">
                <div className="flex justify-between items-start mb-1">
                  <span className="font-medium">
                    Piece {piece.piece_number || index + 1}
                  </span>
                  <div className="text-right">
                    <div className="font-medium">{formatCurrency(piece.piece_cost ?? piece.sub_total_before_min_charge)}</div>
                    <div className="text-gray-500 text-[10px]">
                      {piece.quantity ?? '—'} × {formatCurrency(piece.piece_cost ?? piece.sub_total_before_min_charge)}
                    </div>
                  </div>
                </div>

                <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2 text-[10px]">
                  <dt className="text-gray-600">Job Type:</dt>
                  <dd className="text-right">
                    {(() => {
                      const jobTypeName = piece.job_type || '—';
                      // Check if Dreams job with plain board
                      const isDreamsPlainBoard =
                        jobTypeName.toLowerCase() === 'dreams' &&
                        (piece.dreams_note?.includes('Plain') ||
                         (piece.materials?.length === 1 && piece.materials[0]?.id === '5mm-foamex'));

                      return isDreamsPlainBoard
                        ? `${jobTypeName} - plain board, no printing`
                        : jobTypeName;
                    })()}
                  </dd>
                  <dt className="text-gray-600">Material:</dt>
                  <dd className="text-right">
                    {piece.materials?.map((m) => m.name).join(', ') ||
                      piece.material?.name ||
                      'Unknown'}
                  </dd>
                  <dt className="text-gray-600">Dimensions:</dt>
                  <dd className="text-right">
                    {piece.dimensions ||
                      (piece.width_mm && piece.height_mm
                        ? `${piece.width_mm} × ${piece.height_mm}mm`
                        : '—')}
                  </dd>
                  <dt className="text-gray-600">Material Cost:</dt>
                  <dd className="text-right">
                    {formatCurrency(piece.materials_cost)}
                  </dd>
                </dl>

                {piece.finishing_processes && piece.finishing_processes.length > 0 && (
                  <div className="mt-1 pt-1 border-t border-gray-100">
                    <h6 className="font-medium text-gray-600 mb-0.5">Finishing:</h6>
                    <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2 text-[10px]">
                      {piece.finishing_processes.map((finish, finishIndex) => (
                        <React.Fragment key={finishIndex}>
                          <dt className="text-gray-600">
                            {finish.name}
                            {finish.weeding_complexity_label && (
                              <span className="text-gray-500 font-normal text-xs ml-1">
                                (Complexity: {finish.weeding_complexity_label})
                              </span>
                            )}
                            :
                          </dt>
                          <dd className="text-right">
                            {formatCurrency(finish.total_cost)}
                          </dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* --- Partner Tier Pricing (Multi-Piece Large Format) --- */}
        {pricingTier === 'partner' && breakdown.partner_tier_details && (
          <div className="pt-1 mt-1 border-t border-gray-200">
            <h5 className="font-medium text-gray-600 mb-0.5">Partner Pricing:</h5>
            <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
              <dt className="text-gray-600 font-medium">Retail Price:</dt>
              <dd className="text-right font-medium">{formatCurrency(breakdown.partner_tier_details.original_retail_price)}</dd>
              <dt className="text-green-600">Partner Discount ({breakdown.partner_tier_details.discount_percent || ((1 - breakdown.partner_tier_details.discount_factor) * 100).toFixed(1)}%):</dt>
              <dd className="text-right text-green-600">-{formatCurrency(breakdown.partner_tier_details.original_retail_price - breakdown.partner_tier_details.price_after_discount_before_min_charge)}</dd>
              <dt className="text-green-700 font-semibold">Partner Price:</dt>
              <dd className="text-right text-green-700 font-semibold">{formatCurrency(breakdown.total_cost)}</dd>
            </dl>
          </div>
        )}
      </div>
    );
  }

  // --- NCR Specific Breakdown ---
  if (breakdown.is_ncr) {
    // Check if this NCR job uses the new curve margin pricing (retail/partner) or old volume discount pricing (trade)
    const useCurveMargin = breakdown.curve_margin_applied === true;

    return (
      <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs space-y-2">
        {/* --- Material Costs (NCR) --- */}
        {breakdown.material_cost_details && breakdown.material_cost_details.length > 0 && (
          <>
            <h5 className="font-medium text-gray-600 mb-0.5">Material Costs:</h5>
            <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
              {useCurveMargin ? (
                // NEW FLOW: Curve Margin (Retail/Partner) - Show simplified base costs only
                <>
                  {breakdown.material_cost_details.map((part, index) => {
                    const clickCostData = breakdown.click_cost_per_part?.find(
                      (c) => c.part_number === part.part_number
                    );
                    const sidesLabel = clickCostData && clickCostData.sides > clickCostData.sheets
                      ? 'Double-sided'
                      : 'Single-sided';
                    const printerLabel = clickCostData?.printer || 'Unknown';

                    return (
                      <React.Fragment key={`ncr-mat-${index}`}>
                        <dt className="text-gray-600 font-medium col-span-2 pt-1 border-t mt-1">
                          Part {part.part_number}: {part.name}
                        </dt>
                        <dt className="text-gray-500 pl-2">└ Sheets Needed:</dt>
                        <dd className="text-right">{part.sheets_needed}</dd>
                        <dt className="text-gray-500 pl-2">└ Base Cost:</dt>
                        <dd className="text-right">{formatCurrency(part.base_cost_total)}</dd>
                        {clickCostData && (
                          <>
                            <dt className="text-gray-500 pl-2">└ Sides:</dt>
                            <dd className="text-right">{sidesLabel}</dd>
                            <dt className="text-gray-500 pl-2">└ Printer:</dt>
                            <dd className="text-right">{printerLabel} - {formatCurrency(clickCostData.click_cost)}</dd>
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <dt className="text-gray-600 font-medium col-span-2 pt-1 border-t mt-1">Total Material Cost:</dt>
                  <dd className="text-right font-medium col-start-2">{formatCurrency(breakdown.base_total_material_cost_before_markup ?? breakdown.material_cost_details.reduce((sum, part) => sum + (part.base_cost_total ?? 0), 0))}</dd>
                </>
              ) : (
                // OLD FLOW: Volume Discount (Trade) - Show detailed breakdown with discounts and markups
                <>
                  {breakdown.material_cost_details.map((part, index) => {
                    const clickCostData = breakdown.click_cost_per_part?.find(
                      (c) => c.part_number === part.part_number
                    );
                    const sidesLabel = clickCostData && clickCostData.sides > clickCostData.sheets
                      ? 'Double-sided'
                      : 'Single-sided';
                    const printerLabel = clickCostData?.printer || 'Unknown';

                    return (
                      <React.Fragment key={`ncr-mat-${index}`}>
                        <dt className="text-gray-600 font-medium col-span-2 pt-1 border-t mt-1">
                          Part {part.part_number}: {part.name}
                        </dt>
                        <dt className="text-gray-500 pl-2">└ Sheets Needed:</dt>
                        <dd className="text-right">{part.sheets_needed}</dd>
                        <dt className="text-gray-500 pl-2">└ Base Cost:</dt>
                        <dd className="text-right">{formatCurrency(part.base_cost_total)}</dd>
                        {part.discount_info && (
                          <>
                            <dt className="text-gray-500 pl-2">└ Volume Discount:</dt>
                            <dd className="text-right text-green-600">-{formatCurrency(part.discount_info.discount_amount)}</dd>
                            <dt className="text-gray-500 pl-2">└ Discounted Cost:</dt>
                            <dd className="text-right">{formatCurrency(part.discount_info.final_cost_before_markup)}</dd>
                          </>
                        )}
                        <dt className="text-gray-500 pl-2">└ Markup ({part.markup_percent}%):</dt>
                        <dd className="text-right">{formatCurrency(part.total_cost - (part.discount_info?.final_cost_before_markup ?? part.base_cost_total))}</dd>
                        <dt className="pl-2">└ Total Marked Up:</dt>
                        <dd className="text-right font-medium">{formatCurrency(part.total_cost)}</dd>
                        {clickCostData && (
                          <>
                            <dt className="text-gray-500 pl-2">└ Sides:</dt>
                            <dd className="text-right">{sidesLabel}</dd>
                            <dt className="text-gray-500 pl-2">└ Printer:</dt>
                            <dd className="text-right">{printerLabel} - {formatCurrency(clickCostData.click_cost)}</dd>
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <dt className="text-gray-600 font-medium col-span-2 pt-1 border-t mt-1">Total Material Cost:</dt>
                  <dd className="text-right font-medium col-start-2">{formatCurrency(breakdown.material_cost)}</dd>
                </>
              )}
            </dl>
          </>
        )}

        {/* --- Printing Costs (NCR) --- */}
        <div className="pt-1 mt-1 border-t border-gray-200">
          <h5 className="font-medium text-gray-600 mb-0.5">Printing Costs:</h5>
          {breakdown.colour_click_rate_applied_to_all_parts && (
            <p className="text-gray-500 mb-1" data-testid="ncr-colour-click-rate-note">
              All layers are charged at the colour click rate because this set includes a colour layer.
            </p>
          )}
          <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
            {useCurveMargin ? (
              // NEW FLOW: Curve Margin (Retail/Partner) - Show simplified base click cost only
              <>
                <dt className="text-gray-600">Click Cost ({breakdown.total_sides_for_click ?? 'N/A'} sides):</dt>
                <dd className="text-right">{formatCurrency(breakdown.base_click_cost ?? breakdown.click_cost)}</dd>
              </>
            ) : (
              // OLD FLOW: Volume Discount (Trade) - Show detailed breakdown
              <>
                {breakdown.click_cost_discount_info ? (
                  <>
                    <dt className="text-gray-600">Original Click Cost ({breakdown.total_sides_for_click ?? 'N/A'} sides):</dt>
                    <dd className="text-right line-through text-gray-500">
                      {formatCurrency(breakdown.click_cost_discount_info.original_total_click_cost)}
                    </dd>
                    <dt className="text-gray-600">Volume Discount:</dt>
                    <dd className="text-right text-green-600">
                      -{formatCurrency(breakdown.click_cost_discount_info.discount_amount)}
                    </dd>
                    <dt className="text-gray-600">Discounted Click Cost:</dt>
                    <dd className="text-right">{formatCurrency(breakdown.click_cost_discount_info.final_total_click_cost_before_markup)}</dd>
                    <dt className="text-gray-600">Marked-up Click Cost:</dt>
                    <dd className="text-right font-medium">{formatCurrency(breakdown.click_cost)}</dd>
                  </>
                ) : (
                  <>
                    <dt className="text-gray-600">Click Cost ({breakdown.total_sides_for_click ?? 'N/A'} sides):</dt>
                    <dd className="text-right">{formatCurrency(breakdown.click_cost)}</dd>
                  </>
                )}
              </>
            )}
          </dl>
        </div>

        {/* --- Retail Margin Adjustment (NEW FLOW ONLY) --- */}
        {useCurveMargin && breakdown.base_cost_for_curve_margin !== undefined && (
          <div className="pt-1 mt-1 border-t border-gray-200">
            <h5 className="font-medium text-gray-600 mb-0.5">Retail Margin Adjustment:</h5>
            <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
              <dt className="text-gray-600">Base Cost (Material + Print) for Curve:</dt>
              <dd className="text-right">{formatCurrency(breakdown.base_cost_for_curve_margin)}</dd>
              <dt className="text-gray-600">Retail Curve Margin ({breakdown.curve_margin_percent?.toFixed(2)}%):</dt>
              <dd className="text-right">+ {formatCurrency((breakdown.marked_up_cost_with_curve || 0) - (breakdown.base_cost_for_curve_margin || 0))}</dd>
              <dt className="text-gray-600 font-medium">Total Material & Print (with Curve):</dt>
              <dd className="text-right font-medium">{formatCurrency(breakdown.marked_up_cost_with_curve)}</dd>
            </dl>
          </div>
        )}

        {/* --- Finishing Costs (NCR) --- */}
        {breakdown.finishing_details?.inner_finishes && breakdown.finishing_details.inner_finishes.length > 0 && (
          <div className="pt-1 mt-1 border-t border-gray-200">
            <h5 className="font-medium text-gray-600 mb-0.5">Finishing:</h5>
            <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
              {breakdown.finishing_details.inner_finishes.map((process, index) => (
                <React.Fragment key={`ncr-finish-${index}`}>
                  <dt>{process.name}</dt>
                  <dd className="text-right">{formatCurrency(process.total_cost)}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        )}

        {/* --- Setup Cost (NCR) --- */}
        {breakdown.setup_cost > 0 && (
          <div className="pt-1 mt-1 border-t border-gray-200">
            <h5 className="font-medium text-gray-600 mb-0.5">Setup Cost:</h5>
            <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
              <dt className="text-gray-600">Setup:</dt>
              <dd className="text-right">{formatCurrency(breakdown.setup_cost)}</dd>
            </dl>
          </div>
        )}

        {/* --- Partner Tier Pricing (NCR) --- */}
        {pricingTier === 'partner' && breakdown.partner_tier_details && (
          <div className="pt-1 mt-1 border-t border-gray-200">
            <h5 className="font-medium text-gray-600 mb-0.5">Partner Pricing:</h5>
            <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
              <dt className="text-gray-600 font-medium">Retail Price:</dt>
              <dd className="text-right font-medium">{formatCurrency(breakdown.partner_tier_details.original_retail_price)}</dd>
              <dt className="text-green-600">Partner Discount ({breakdown.partner_tier_details.discount_percent || ((1 - breakdown.partner_tier_details.discount_factor) * 100).toFixed(1)}%):</dt>
              <dd className="text-right text-green-600">-{formatCurrency(breakdown.partner_tier_details.original_retail_price - breakdown.partner_tier_details.price_after_discount_before_min_charge)}</dd>
              <dt className="text-green-700 font-semibold">Partner Price:</dt>
              <dd className="text-right text-green-700 font-semibold">{formatCurrency(breakdown.total_cost)}</dd>
            </dl>
          </div>
        )}

        {/* Misc Costs and Min Charge for NCR can be handled by the common sections below if keys match */}
      </div>
    );
  }
  // --- END NCR Specific Breakdown ---

  return (
    <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs space-y-2">

      {/* --- Dreams Job Type Indicator --- */}
      {breakdown.dreams_note && (
        <div className="pb-1 mb-1 border-b border-gray-200">
          <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
            <dt className="text-gray-600">Job Type:</dt>
            <dd className="text-right font-medium">
              {breakdown.dreams_note?.includes('Plain')
                ? 'Dreams - plain board, no printing'
                : 'Dreams'}
            </dd>
          </dl>
        </div>
      )}

      {/* --- Large Format: Job Setup Charge --- */}
      {job_setup_charge !== undefined && breakdown.materials && Array.isArray(breakdown.materials) && (
        <div className="pb-1 mb-1 border-b border-gray-200">
          <h5 className="font-medium text-gray-600 mb-0.5">Base Charges:</h5>
          <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
            <dt className="text-gray-600">Job Setup Charge:</dt>
            <dd className="text-right font-medium">{formatCurrency(job_setup_charge)}</dd>
          </dl>
        </div>
      )}




      {/* --- Volume Discount Information (Trade Users Only) --- */}
{!isRetailOrPartner && (breakdown.material_cost_discount || breakdown.print_cost_discount) && (
  <div className="pt-1 mt-1 border-t border-gray-200">
    <h5 className="font-medium text-gray-600 mb-0.5">Volume Discounts:</h5>
    <div className="bg-green-50 p-2 rounded border border-green-100 text-sm">
      <p className="font-medium text-green-700 mb-1">
        {`Volume Discount Applied (${breakdown.total_sheets ?? 'N/A'} total sheets)`}
      </p>
      
      {breakdown.material_cost_discount && (
        <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
          <dt className="text-gray-600">Material Cost Discount:</dt>
          <dd className="text-right text-green-600">
            -{formatCurrency(breakdown.material_cost_discount.discount_amount)} 
            ({breakdown.material_cost_discount.discount_percent}%)
          </dd>
        </dl>
      )}
      
      {breakdown.print_cost_discount ? (
        <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
          <dt className="text-gray-600">Print Cost Discount:</dt>
          <dd className="text-right text-green-600">
            -{formatCurrency(breakdown.print_cost_discount.discount_amount)} 
            ({breakdown.print_cost_discount.discount_percent}%)
          </dd>
        </dl>
      ) : breakdown.click_cost_discount && (
        <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
          <dt className="text-gray-600">Click Cost Discount:</dt>
          <dd className="text-right text-green-600">
            -{formatCurrency(breakdown.click_cost_discount.discount_amount)} 
            ({breakdown.click_cost_discount.discount_percent}%)
          </dd>
        </dl>
      )}
      
      {/* Calculate and show total savings */}
      {((breakdown.material_cost_discount && breakdown.print_cost_discount) || 
        (breakdown.material_cost_discount && breakdown.click_cost_discount)) && (
        <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2 pt-1 mt-1 border-t border-green-100">
          <dt className="text-gray-700 font-medium">Total Savings:</dt>
          <dd className="text-right text-green-700 font-medium">
            -{formatCurrency(
              (breakdown.material_cost_discount ? breakdown.material_cost_discount.discount_amount : 0) +
              (breakdown.print_cost_discount ? breakdown.print_cost_discount.discount_amount : 0) +
              (breakdown.click_cost_discount ? breakdown.click_cost_discount.discount_amount : 0)
            )}
          </dd>
        </dl>
      )}
    </div>
  </div>
)}

      {/* --- Material Costs (Handles Both Formats) --- */}
      <h5 className="font-medium text-gray-600 mb-0.5">Material Costs:</h5>
      <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
        {/* Check if it's a Large Format breakdown (has 'materials' array) */}
        {breakdown.materials && Array.isArray(breakdown.materials) ? (
          <>
            {breakdown.materials.map((material, index) => (
              <React.Fragment key={index}>
                <dt className="text-gray-600 font-medium col-span-2 pt-1 border-t mt-1">
                  {material.name || 'Unknown Material'}
                  {material.linear_meters && ` (${material.linear_meters}m)`}
                  {material.sqm && ` (${material.sqm} sqm)`}
                </dt>
                <dt className="text-gray-500 pl-2">└ Base Cost:</dt>
                <dd className="text-right">{formatCurrency(material.base_cost)}</dd>
                {/* Calculate markup */}
                {(material.total_cost - material.base_cost) > 0.005 && (
                  <>
                    <dt className="text-gray-500 pl-2">└ Markup ({material.markup_percent}%):</dt>
                    <dd className="text-right">{formatCurrency(material.total_cost - material.base_cost)}</dd>
                  </>
                )}
                <dt className="pl-2">└ Total Marked Up:</dt>
                <dd className="text-right font-medium">{formatCurrency(material.total_cost)}</dd>
                {/* Display warning if material doesn't fit roll */}
                {material.warning && (
                    <dd className="col-span-2 text-xs text-orange-600 pl-2">{material.warning}</dd>
                )}
              </React.Fragment>
            ))}
            {/* Optional: Display overall total for large format */}
            <dt className="text-gray-600 font-medium col-span-2 pt-1 border-t mt-1">Total Materials:</dt>
            <dd className="text-right font-medium col-start-2">{formatCurrency(breakdown.materials_cost)}</dd>
          </>
        ) : (
          /* --- ELSE: Assume Small Format breakdown --- */
          <>
            {/* --- Inner Page Material (Small Format) --- */}
            {total_inner_sheets > 0 && (
              <>
                <dt className="text-gray-600 font-medium col-span-2">Text Stock ({breakdown.paper_stock || 'N/A'}):</dt>
                {isRetailOrPartner ? (
                  // Retail/Partner: Show base costs only
                  <>
                    <dt className="text-gray-500 pl-2">└ Base Cost:</dt>
                    <dd className="text-right">{formatCurrency(material_cost)}</dd>
                    <dt className="text-gray-500 pl-2">└ Sheets:</dt>
                    <dd className="text-right text-gray-500">{total_inner_sheets}</dd>
                  </>
                ) : (
                  // Trade: Show detailed breakdown with volume discounts and markups
                  <>
                    {/* Show original cost if there was a volume discount */}
                    {breakdown.material_cost_discount ? (
                      <>
                        <dt className="text-gray-500 pl-2">└ Original Cost:</dt>
                        <dd className="text-right line-through text-gray-500">
                          {formatCurrency(breakdown.material_cost_discount.original)}
                        </dd>
                        <dt className="text-gray-500 pl-2">└ Volume Discount:</dt>
                        <dd className="text-right text-green-600">
                          -{formatCurrency(breakdown.material_cost_discount.discount_amount)}
                        </dd>
                        <dt className="pl-2">└ Discounted Cost:</dt>
                        <dd className="text-right">
                          {formatCurrency(breakdown.material_cost_discount.final_cost_before_markup)} {/* Use cost before markup */}
                        </dd>
                      </>
                    ) : (
                      <>
                        <dt className="text-gray-500 pl-2">└ Base Cost:</dt>
                        <dd className="text-right">{formatCurrency(material_cost)}</dd>
                      </>
                    )}
                    {/* Calculate markup based on final vs discounted base */}
                    {((marked_up_material_cost ?? 0) - (breakdown.material_cost_discount?.final_cost_before_markup ?? material_cost ?? 0)) > 0.005 && (
                      <>
                        <dt className="text-gray-500 pl-2">└ Markup:</dt>
                        <dd className="text-right">{formatCurrency((marked_up_material_cost ?? 0) - (breakdown.material_cost_discount?.final_cost_before_markup ?? material_cost ?? 0))}</dd>
                      </>
                    )}
                    <dt className="pl-2">└ Total Marked Up:</dt>
                    <dd className="text-right font-medium">{formatCurrency(marked_up_material_cost)}</dd>
                    <dt className="text-gray-500 pl-2">└ Sheets:</dt>
                    <dd className="text-right text-gray-500">{total_inner_sheets}</dd>
                  </>
                )}
              </>
            )}

            {/* --- Cover Stock Material (Small Format - Conditional) --- */}
            {cover_stock_details && total_cover_sheets > 0 && (
              <>
                {(() => {
                  const coverType = innerFinishingOptions.wirebind?.cover_type;
                  const coverTypeLabel = coverType === 'front_only'
                    ? ' - Front Only'
                    : coverType === 'front_and_back'
                    ? ' - Front & Back'
                    : '';
                  return (
                    <dt className="text-gray-600 font-medium col-span-2 pt-1 border-t mt-1">Cover Stock ({cover_stock_details.name || 'N/A'}){coverTypeLabel}:</dt>
                  );
                })()}
                {isRetailOrPartner ? (
                  // Retail/Partner: Show base costs only
                  <>
                    <dt className="text-gray-500 pl-2">└ Base Cost:</dt>
                    <dd className="text-right">{formatCurrency(cover_stock_details.base_cost)}</dd>
                    <dt className="text-gray-500 pl-2">└ Sheets:</dt>
                    <dd className="text-right text-gray-500">{total_cover_sheets ?? cover_stock_details.sheets_needed ?? 'N/A'}</dd>
                  </>
                ) : (
                  // Trade: Show detailed breakdown with volume discounts and markups
                  <>
                    {/* Show original cost if there was a volume discount */}
                    {cover_stock_details.discount_info ? (
                      <>
                        <dt className="text-gray-500 pl-2">└ Original Cost:</dt>
                        <dd className="text-right line-through text-gray-500">
                          {formatCurrency(cover_stock_details.discount_info.original)}
                        </dd>
                        <dt className="text-gray-500 pl-2">└ Volume Discount:</dt>
                        <dd className="text-right text-green-600">
                          -{formatCurrency(cover_stock_details.discount_info.discount_amount)}
                        </dd>
                        <dt className="pl-2">└ Discounted Cost:</dt>
                        <dd className="text-right">
                          {formatCurrency(cover_stock_details.discount_info.final_cost_before_markup)} {/* Use cost before markup */}
                        </dd>
                      </>
                    ) : (
                      <>
                        <dt className="text-gray-500 pl-2">└ Base Cost:</dt>
                        <dd className="text-right">{formatCurrency(cover_stock_details.base_cost)}</dd>
                      </>
                    )}
                    {/* Calculate cover markup based on final vs discounted base */}
                    {((cover_stock_details.total_cost ?? 0) - (cover_stock_details.discount_info?.final_cost_before_markup ?? cover_stock_details.base_cost ?? 0)) > 0.005 && (
                       <>
                         <dt className="text-gray-500 pl-2">└ Markup ({cover_stock_details.markup_percent}%):</dt>
                         <dd className="text-right">{formatCurrency((cover_stock_details.total_cost ?? 0) - (cover_stock_details.discount_info?.final_cost_before_markup ?? cover_stock_details.base_cost ?? 0))}</dd>
                       </>
                    )}
                    <dt className="pl-2">└ Total Marked Up:</dt>
                    <dd className="text-right font-medium">{formatCurrency(cover_stock_details.total_cost)}</dd>
                    <dt className="text-gray-500 pl-2">└ Sheets:</dt>
                    <dd className="text-right text-gray-500">{total_cover_sheets ?? cover_stock_details.sheets_needed ?? 'N/A'}</dd>
                  </>
                )}
              </>
            )}
            {/* --- End Cover Stock Material --- */}
          </>
        )}
      </dl>


             {/* --- Printing Costs (Handles Both Formats) --- */}
       <div className="pt-1 mt-1 border-t border-gray-200">
         <h5 className="font-medium text-gray-600 mb-0.5">Printing Costs:</h5>
        <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
          {/* Check if Large Format (has print_setup_cost) */}
          {breakdown.print_setup_cost !== undefined ? (
            <>
              <dt className="text-gray-600">Print Setup Cost:</dt>
              <dd className="text-right">{formatCurrency(breakdown.print_setup_cost)}</dd>
              <dt className="text-gray-600">Print Run Cost:</dt>
              <dd className="text-right">{formatCurrency(breakdown.print_run_cost)}</dd>
              {/* Optional: Display total print cost if needed, though it's implied */}
              {/* <dt className="text-gray-600 font-medium">Total Print Cost:</dt>
              <dd className="text-right font-medium">{formatCurrency(breakdown.print_cost)}</dd> */}
            </>
          ) : (
            /* ELSE: Assume Small Format */
            <>
                             {isRetailOrPartner ? (
                 // Retail/Partner: Show base costs only
                 <>
                   <dt className="text-gray-600">Click Cost ({total_sides ?? total_sides_for_click ?? 'N/A'} sides):</dt>
                   <dd className="text-right">{formatCurrency(base_total_click_cost || click_cost || 0)}</dd>
                 </>
               ) : (
                // Trade: Show detailed breakdown with volume discounts and markups
                <>
                  {/* Show original click cost if there was a volume discount - CORRECTED */}
                  {breakdown.click_cost_discount_info ? (
                    <>
                      <dt className="text-gray-600">Original Click Cost ({total_sides ?? 'N/A'} sides):</dt>
                      <dd className="text-right line-through text-gray-500">
                        {formatCurrency(breakdown.click_cost_discount_info.original_total_click_cost)}
                      </dd>
                      <dt className="text-gray-600">Volume Discount:</dt>
                      <dd className="text-right text-green-600">
                        -{formatCurrency(breakdown.click_cost_discount_info.discount_amount)}
                      </dd>
                      <dt className="text-gray-600">Discounted Click Cost:</dt>
                      <dd className="text-right">{formatCurrency(breakdown.click_cost_discount_info.final_total_click_cost_before_markup)}</dd>
                      <dt className="text-gray-600">Marked-up Click Cost:</dt>
                      <dd className="text-right font-medium">{formatCurrency(click_cost)}</dd>
                    </>
                  ) : (
                    <>
                      <dt className="text-gray-600">Click Cost ({total_sides ?? total_sides_for_click ?? 'N/A'} sides):</dt>
                      <dd className="text-right">{formatCurrency(click_cost || 0)}</dd>
                    </>
                  )}
                  
                </>
              )}
            </>
          )}
        </dl>
      </div>

      {/* --- Curve Margin (Retail/Partner Only) --- */}
      {isRetailOrPartner && curve_margin_applied && (
        <div className="pt-1 mt-1 border-t border-gray-200">
          <h5 className="font-medium text-gray-600 mb-0.5">Retail Margin Adjustment:</h5>
          <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
            <dt className="text-gray-600">Base Cost (Material + Print) for Curve:</dt>
            <dd className="text-right">{formatCurrency(base_cost_for_curve_margin)}</dd>
            <dt className="text-gray-600">Retail Curve Margin ({curve_margin_percent?.toFixed(1)}%):</dt>
            <dd className="text-right">+ {formatCurrency((marked_up_cost_with_curve || 0) - (base_cost_for_curve_margin || 0))}</dd>
            <dt className="text-gray-600 font-medium">Total Material & Print (with Curve):</dt>
            <dd className="text-right font-medium">{formatCurrency(marked_up_cost_with_curve)}</dd>
          </dl>
        </div>
      )}

      {/* --- Finishing Costs (Handles Both Formats) --- */}
      {materialIncludedLaminate || largeFormatFinishes.length > 0 || (finishing && finishing.length > 0) ? (
        <div className="pt-1 mt-1 border-t border-gray-200">
          <h5 className="font-medium text-gray-600 mb-0.5">Finishing:</h5>
          <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
            {/* Check if Large Format (has finishing_processes) */}
            {materialIncludedLaminate || largeFormatFinishes.length > 0 ? (
              <>
                {largeFormatFinishes.map((process, index) => (
                  <React.Fragment key={index}>
                    <dt className="col-span-2 font-medium pt-1">
                      {process.name}
                      {process.weeding_complexity_label && (
                        <span className="text-gray-600 font-normal text-sm ml-2">
                          (Complexity: {process.weeding_complexity_label})
                        </span>
                      )}
                      :
                    </dt>
                    {process.setup_cost > 0 && (
                       <>
                         <dt className="text-gray-500 pl-2">└ Setup:</dt>
                         <dd className="text-right">{formatCurrency(process.setup_cost)}</dd>
                       </>
                    )}
                    {process.run_on_details && process.run_on_details !== "No run-on costs" && (
                       <>
                         <dt className="text-gray-500 pl-2">└ Run-on:</dt>
                         {/* Display details on a new line for clarity */}
                         <dd className="text-right col-span-2 text-gray-500 text-[10px] leading-tight">{process.run_on_details}</dd>
                       </>
                    )}
                     <dt className="pl-2">└ Total:</dt>
                     <dd className="text-right font-medium">{formatCurrency(process.total_cost)}</dd>
                     {process.warning && (
                        <dd className="col-span-2 text-xs text-orange-600 pl-2">{process.warning}</dd>
                     )}
                  </React.Fragment>
                ))}
                 {materialIncludedLaminate && (
                   <>
                     <dt className="col-span-2 font-medium pt-1">{getLargeFormatLaminateLabel(item)}</dt>
                     <dd className="col-span-2 pl-2 text-gray-500">Cost included in materials</dd>
                   </>
                 )}
                 {/* Optional: Display overall total finishing cost for large format */}
                 <dt className="text-gray-600 font-medium col-span-2 pt-1 border-t mt-1">Total Finishing:</dt>
                 <dd className="text-right font-medium col-start-2">{formatCurrency(breakdown.finishing_cost)}</dd>
              </>
            ) : (
              /* ELSE: Assume Small Format */
              <>
                {(hasInnerFinishesDetailed || hasCoverFinishesDetailed) ? (
                  <>
                    {hasInnerFinishesDetailed && (
                      <>
                        <dt className="col-span-2 font-medium pt-1">Inner Finishing:</dt>
                        {innerFinishesDetailed.map((process, index) => {
                          const optionSummary = formatFinishOptions(process);
                          return (
                            <React.Fragment key={`inner-finish-${index}`}>
                              <dt className="pl-2">{process.name}</dt>
                              <dd className="text-right">{formatCurrency(process.total_cost)}</dd>
                              {optionSummary && (
                                <dd className="col-span-2 text-gray-500 text-[10px] pl-4">Options: {optionSummary}</dd>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </>
                    )}
                    {hasCoverFinishesDetailed && (
                      <>
                        <dt className="col-span-2 font-medium pt-1 border-t border-gray-200 mt-1">Cover Finishing:</dt>
                        {coverFinishesDetailed.map((process, index) => {
                          const optionSummary = formatFinishOptions(process);
                          return (
                            <React.Fragment key={`cover-finish-${index}`}>
                              <dt className="pl-2">{process.name}</dt>
                              <dd className="text-right">{formatCurrency(process.total_cost)}</dd>
                              {optionSummary && (
                                <dd className="col-span-2 text-gray-500 text-[10px] pl-4">Options: {optionSummary}</dd>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </>
                    )}
                  </>
                ) : (
                  finishing.map((process, index) => (
                    <React.Fragment key={index}>
                      <dt>{process.name}</dt>
                      <dd className="text-right">{formatCurrency(process.total_cost)}</dd>
                    </React.Fragment>
                  ))
                )}
              </>
            )}
          </dl>
        </div>
             ) : null /* Render nothing if no finishing costs for either format */}

      {printingNotes.length > 0 && (
        <div className="pt-1 mt-1 border-t border-gray-200">
          <h5 className="font-medium text-gray-600 mb-0.5">Printing Notes:</h5>
          <ul className="list-disc pl-6 text-xs text-gray-600 space-y-0.5">
            {printingNotes.map((note, idx) => (
              <li key={`printing-note-${idx}`}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {/* --- Volume Discount Information (Large Format) --- */}
      {breakdown.volume_discount_amount > 0 && (
        <div className="pt-1 mt-1 border-t border-gray-200">
          <h5 className="font-medium text-gray-600 mb-0.5">Volume Discount:</h5>
          <div className="bg-green-50 p-2 rounded border border-green-100">
            <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2 text-xs">
              <dt className="text-green-700 font-medium">Total Savings:</dt>
              <dd className="text-right text-green-600 font-semibold">
                -£{formatNumber(breakdown.volume_discount_amount, 2)}
              </dd>

              {breakdown.volume_discount_breakdown && breakdown.volume_discount_breakdown.length > 0 && (
                <>
                  <dt className="col-span-2 text-gray-600 text-[10px] mt-0.5 mb-0.5">Tier Breakdown:</dt>
                  {breakdown.volume_discount_breakdown.map((entry, idx) => (
                    renderVolumeDiscountEntry(entry, idx)
                  ))}
                </>
              )}

              <dt className="text-gray-700 font-medium border-t border-green-200 pt-1 mt-1">
                After volume discount:
              </dt>
              <dd className="text-right font-medium border-t border-green-200 pt-1 mt-1">
                £{formatNumber(breakdown.subtotal_after_volume_discount || breakdown.sub_total_before_min_charge, 2)}
              </dd>
            </dl>
          </div>
        </div>
      )}

       {/* --- Setup Cost --- */}
       {setup_cost > 0 && (
         <div className="pt-1 mt-1 border-t border-gray-200">
           <h5 className="font-medium text-gray-600 mb-0.5">Setup Cost:</h5>
           <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
             {/* Show breakdown if we have design-specific details */}
             {breakdown.design_setup_cost !== undefined && breakdown.number_of_designs > 1 ? (
               <>
                 <dt className="text-gray-600">Base Setup:</dt>
                 <dd className="text-right">{formatCurrency((setup_cost || 0) - (breakdown.design_setup_cost || 0))}</dd>
                 <dt className="text-gray-600">Design Setup ({breakdown.number_of_designs} designs):</dt>
                 <dd className="text-right">{formatCurrency(breakdown.design_setup_cost)}</dd>
                 {breakdown.cost_per_design && (
                   <>
                     <dt className="text-gray-500 pl-2">└ Cost per design:</dt>
                     <dd className="text-right text-gray-500">{formatCurrency(breakdown.cost_per_design)}</dd>
                   </>
                 )}
                 <dt className="text-gray-600 font-medium border-t border-gray-200 pt-1 mt-1">Total Setup:</dt>
                 <dd className="text-right font-medium border-t border-gray-200 pt-1 mt-1">{formatCurrency(setup_cost)}</dd>
               </>
             ) : (
               <>
                 <dt className="text-gray-600">Setup:</dt>
                 <dd className="text-right">{formatCurrency(setup_cost)}</dd>
                 {breakdown.number_of_designs > 1 && (
                   <>
                     <dt className="text-gray-500 pl-2">└ Designs:</dt>
                     <dd className="text-right text-gray-500">{breakdown.number_of_designs}</dd>
                   </>
                 )}
               </>
             )}
           </dl>
         </div>
       )}
 
       {/* --- Miscellaneous Costs --- */}
      {misc_costs && misc_costs.length > 0 && (
        <div className="pt-1 mt-1 border-t border-gray-200">
          <h5 className="font-medium text-gray-600 mb-0.5">Additional Costs:</h5>
          <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
            {misc_costs.map((cost, index) => (
              <React.Fragment key={index}>
                <dt>{cost.name}</dt>
                <dd className="text-right">{formatCurrency(cost.cost)}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      )}

             {/* --- Partner Tier Pricing --- */}
       {pricingTier === 'partner' && partner_tier_details && (
         <div className="pt-1 mt-1 border-t border-gray-200">
           <h5 className="font-medium text-gray-600 mb-0.5">Partner Pricing:</h5>
           <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
             <dt className="text-gray-600 font-medium">Retail Price:</dt>
             <dd className="text-right font-medium">{formatCurrency(partner_tier_details.original_retail_price)}</dd>
             <dt className="text-green-600">Partner Discount ({partner_tier_details.discount_percent || ((1 - partner_tier_details.discount_factor) * 100).toFixed(1)}%):</dt>
             <dd className="text-right text-green-600">-{formatCurrency(partner_tier_details.original_retail_price - partner_tier_details.price_after_discount_before_min_charge)}</dd>
             <dt className="text-green-700 font-semibold">Partner Price:</dt>
             <dd className="text-right text-green-700 font-semibold">{formatCurrency(breakdown.total_cost)}</dd>
           </dl>
         </div>
       )}

       {/* --- Imposition Info (Using Visualizer) --- */}
       {(innerImpositionData || cover_imposition) && ( // Check if EITHER exists
         <div className="pt-1 mt-1 border-t border-gray-200">
           <h5 className="font-medium text-gray-600 mb-0.5">Production Info:</h5>
           <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2"> {/* Grid for side-by-side */}

             {/* --- Inner Page Imposition --- */}
             {innerImpositionData && total_inner_sheets > 0 && (
               <div> {/* Column for inner page info */}
                 <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2 mb-1">
                   <dt className="col-span-2 font-medium text-gray-500">Text Pages:</dt>
                   <dt>Items/Sheet:</dt>
                   <dd className="text-right">{innerImpositionData.items_per_sheet ?? 'N/A'}</dd>
                   <dt>Waste:</dt>
                   <dd className="text-right">{innerImpositionData.waste_percentage?.toFixed(1) ?? 'N/A'}%</dd>
                 </dl>
                 {/* Render Inner Page Visualizer */}
                 <ImpositionVisualizer
                     impositionData={innerImpositionData}
                     title="Sheet Layout"
                     maxWidth="150px" // Make visualizer smaller in breakdown
                 />
               </div>
             )}

             {/* --- Cover Page Imposition --- */}
             {cover_imposition && total_cover_sheets > 0 && (
               <div> {/* Column for cover page info */}
                 <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2 mb-1">
                    <dt className="col-span-2 font-medium text-gray-500">Cover Pages:</dt>
                    <dt>Items/Sheet:</dt>
                    <dd className="text-right">{cover_imposition.items_per_sheet ?? 'N/A'}</dd>
                    <dt>Waste:</dt>
                    <dd className="text-right">{cover_imposition.waste_percentage?.toFixed(1) ?? 'N/A'}%</dd>
                 </dl>
                 {/* Render Cover Page Visualizer */}
                 <ImpositionVisualizer
                     impositionData={cover_imposition}
                     title="Cover Layout"
                     maxWidth="150px" // Make visualizer smaller in breakdown
                 />
               </div>
             )}
           </div> {/* End grid */}
         </div>
       )}

       {/* --- Minimum Charge Applied (Small Format - boolean based) --- */}
       {min_charge_applied && !(breakdown.materials && Array.isArray(breakdown.materials)) && (
         <p className="pt-1 mt-1 border-t border-gray-200 text-red-600 font-medium">
           Minimum charge applied.
         </p>
       )}

       {/* --- Large Format: Subtotal & Minimum Charge Adjustment --- */}
       {breakdown.materials && Array.isArray(breakdown.materials) && sub_total_before_min_charge !== undefined && (
         <div className="pt-1 mt-1 border-t border-gray-200">
           <h5 className="font-medium text-gray-600 mb-0.5">Summary:</h5>
           <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 pl-2">
             <dt className="text-gray-600 font-semibold">Subtotal (before min. charge):</dt>
             <dd className="text-right font-semibold">{formatCurrency(sub_total_before_min_charge)}</dd>

             {minimum_charge_threshold !== undefined && minimum_charge_applied_adjustment !== undefined && (
               <>
                 <dt className="text-orange-600">Min. Charge Adjustment:</dt>
                 <dd className="text-right text-orange-600">
                   + {formatCurrency(minimum_charge_applied_adjustment)}
                   <span className="block text-xs text-gray-500 normal-case">
                     (to reach £{formatCurrency(minimum_charge_threshold)})
                   </span>
                 </dd>
               </>
             )}
           </dl>
         </div>
       )}

      {/* Note: Total cost is usually displayed more prominently outside this component */}
    </div>
  );
};

export default BreakdownDisplay;
